import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { APIConfiguration } from '../types';

interface APIConfigurationSectionProps {
  config: APIConfiguration;
  onUpdate: (config: APIConfiguration) => void;
  isAdmin: boolean;
}

const APIConfigurationSection = ({ config, onUpdate, isAdmin }: APIConfigurationSectionProps) => {
  const [formData, setFormData] = useState(config);
  const [hasChanges, setHasChanges] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  const environmentOptions = [
    { value: 'homologation', label: 'Homologação' },
    { value: 'production', label: 'Produção' }
  ];

  const handleChange = (field: keyof APIConfiguration, value: any) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setTimeout(() => {
      setIsTestingConnection(false);
    }, 2000);
  };

  const handleRenewToken = () => {
    console.log('Renovando token...');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'disconnected':
        return 'text-muted-foreground';
      case 'error':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle2';
      case 'disconnected':
        return 'XCircle';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Configuração de API</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie a integração com Gestor Tributário 3.0
          </p>
        </div>
        {hasChanges && isAdmin && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setFormData(config)}>
              Descartar
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
              Salvar Configurações
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Server" size={20} color="var(--color-primary)" />
            Status da Conexão
          </h3>
          <div className={`flex items-center gap-2 ${getStatusColor(config.connectionStatus)}`}>
            <Icon name={getStatusIcon(config.connectionStatus)} size={20} color="currentColor" />
            <span className="text-sm font-medium capitalize">{config.connectionStatus}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
              <span className="text-sm font-medium text-muted-foreground">Última Sincronização</span>
            </div>
            <p className="text-base font-semibold text-foreground">
              {new Date(config.lastSync).toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Key" size={16} color="var(--color-muted-foreground)" />
              <span className="text-sm font-medium text-muted-foreground">Token Expira Em</span>
            </div>
            <p className="text-base font-semibold text-foreground">
              {new Date(config.tokenExpiresAt).toLocaleString('pt-BR')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={handleTestConnection}
            loading={isTestingConnection}
          >
            Testar Conexão
          </Button>
          <Button variant="outline" iconName="Key" iconPosition="left" onClick={handleRenewToken}>
            Renovar Token
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          Configurações de Ambiente
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Ambiente"
            description="Selecione o ambiente de integração"
            options={environmentOptions}
            value={formData.environment}
            onChange={(value) => handleChange('environment', value)}
            disabled={!isAdmin}
          />

          <Input
            label="URL Base da API"
            type="text"
            value={formData.baseUrl}
            onChange={(e) => handleChange('baseUrl', e.target.value)}
            disabled={!isAdmin}
            description="Endpoint principal da API"
          />

          <Input
            label="Timeout (segundos)"
            type="number"
            value={formData.timeout}
            onChange={(e) => handleChange('timeout', Number(e.target.value))}
            disabled={!isAdmin}
            min={5}
            max={300}
          />

          <Input
            label="Tentativas de Retry"
            type="number"
            value={formData.retryAttempts}
            onChange={(e) => handleChange('retryAttempts', Number(e.target.value))}
            disabled={!isAdmin}
            min={0}
            max={10}
          />

          <Input
            label="Limite de Requisições/Minuto"
            type="number"
            value={formData.rateLimitPerMinute}
            onChange={(e) => handleChange('rateLimitPerMinute', Number(e.target.value))}
            disabled={!isAdmin}
            min={1}
            max={1000}
          />

          <div className="flex items-center">
            <Checkbox
              label="Renovação Automática de Token"
              description="Renovar token antes da expiração"
              checked={formData.autoRenewToken}
              onChange={(e) => handleChange('autoRenewToken', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          Métricas de Performance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Zap" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-muted-foreground">Tempo Médio</span>
            </div>
            <p className="text-2xl font-bold text-foreground">1.2s</p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-muted-foreground">Taxa de Sucesso</span>
            </div>
            <p className="text-2xl font-bold text-foreground">98.5%</p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-muted-foreground">Requisições Hoje</span>
            </div>
            <p className="text-2xl font-bold text-foreground">1,247</p>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Acesso Restrito</p>
              <p className="text-sm text-muted-foreground mt-1">
                Apenas administradores podem modificar as configurações de API. Entre em contato com
                seu supervisor para alterações.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIConfigurationSection;